/**
 * 新規登録者用の管理コンポーネント
 * @returns
 */
import React, { useState, useEffect, useCallback } from 'react';
import { UserInfo } from '../../types/userInfo';
import OnboardingManager from './onboarding/OnboardingManager';
import RegistrationFeedbackModal from './RegistrationFeedbackModal';
import BookmarkGuideModal, { logBookmarkGuideEvent } from './BookmarkGuideModal';
import {
    FIRST_TIME_SHOW_REGISTRATION_FEEDBACK,
    SESSION_FIRST_TIME_AWAITING_BOOKMARK,
    SESSION_PENDING_BOOKMARK_AFTER_ANALYSIS,
    FIRST_TIME_DISMISS_TUTORIAL_OVERLAY,
    SESSION_OPEN_REGISTRATION_FEEDBACK_AFTER_TUTORIAL,
    SESSION_STICKY_NEW_USER,
    FIRST_TIME_ANALYSIS_RESULTS_DISPLAYED,
    BOOKMARK_GUIDE_DELAY_MS,
} from '@/constants/firstTimeExperienceFlow';

const MOBILE_BREAKPOINT = 768;

interface FirstTimeExperienceManagerProps {
    userInfo: UserInfo | null;
    children: React.ReactNode;
}

const FirstTimeExperienceManager: React.FC<FirstTimeExperienceManagerProps> = ({
    userInfo,
    children,
}) => {
    const [showOnboarding, setShowOnboarding] = useState<boolean>(false);
    const [showRegistrationFeedback, setShowRegistrationFeedback] = useState<boolean>(false);
    const [showBookmarkGuide, setShowBookmarkGuide] = useState<boolean>(false);
    const [isMobileViewport, setIsMobileViewport] = useState<boolean>(() =>
        typeof window !== 'undefined' ? window.innerWidth < MOBILE_BREAKPOINT : false,
    );

    // 新規ユーザーかどうかの判定
    useEffect(() => {
        const onboardingCompleted = sessionStorage.getItem('onboarding_completed') === 'true';

        // 初回ログイン（isNewlyRegistered=true）かつオンボーディング未完了の場合のみ表示
        const isNewUser =
            (userInfo?.isNewlyRegistered || sessionStorage.getItem(SESSION_STICKY_NEW_USER) === 'true') &&
            !onboardingCompleted;

        if (isNewUser) {
            setShowOnboarding(true);
        } else {
            setShowOnboarding(false);
        }
    }, [userInfo]);

    // チュートリアル完了→別URLの Manager にマウントし直してもフィードバックを開けるようセッションを参照
    useEffect(() => {
        if (sessionStorage.getItem('registration_feedback_completed') === 'true') {
            sessionStorage.removeItem(SESSION_OPEN_REGISTRATION_FEEDBACK_AFTER_TUTORIAL);
            return;
        }
        if (sessionStorage.getItem(SESSION_OPEN_REGISTRATION_FEEDBACK_AFTER_TUTORIAL) === 'true') {
            setShowRegistrationFeedback(true);
        }
    }, []);

    useEffect(() => {
        const handleOnboardingCompleted = () => {
            setShowOnboarding(false);
        };

        const handleShowRegistrationFeedbackFromTrial = () => {
            setShowRegistrationFeedback(true);
        };

        window.addEventListener('onboarding-completed', handleOnboardingCompleted);
        window.addEventListener(FIRST_TIME_SHOW_REGISTRATION_FEEDBACK, handleShowRegistrationFeedbackFromTrial);

        return () => {
            window.removeEventListener('onboarding-completed', handleOnboardingCompleted);
            window.removeEventListener(
                FIRST_TIME_SHOW_REGISTRATION_FEEDBACK,
                handleShowRegistrationFeedbackFromTrial,
            );
        };
    }, []);

    useEffect(() => {
        const onResize = () => setIsMobileViewport(window.innerWidth < MOBILE_BREAKPOINT);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // チュートリアル終了後の分析で結果が更新されたら、一定時間後にブックマーク案内（子の Onboarding より長生きするようここで購読）
    useEffect(() => {
        let timeoutId: number | undefined;
        const onAnalysisDisplayed = () => {
            if (timeoutId !== undefined) {
                window.clearTimeout(timeoutId);
            }
            timeoutId = window.setTimeout(() => {
                setShowBookmarkGuide(true);
                timeoutId = undefined;
            }, BOOKMARK_GUIDE_DELAY_MS);
        };
        window.addEventListener(FIRST_TIME_ANALYSIS_RESULTS_DISPLAYED, onAnalysisDisplayed);
        return () => {
            window.removeEventListener(FIRST_TIME_ANALYSIS_RESULTS_DISPLAYED, onAnalysisDisplayed);
            if (timeoutId !== undefined) {
                window.clearTimeout(timeoutId);
            }
        };
    }, []);

    const closeBookmarkGuide = useCallback(() => {
        setShowBookmarkGuide(false);
        sessionStorage.removeItem(SESSION_PENDING_BOOKMARK_AFTER_ANALYSIS);
        void logBookmarkGuideEvent();
    }, []);

    const completeRegistrationFeedback = () => {
        setShowRegistrationFeedback(false);
        sessionStorage.setItem('registration_feedback_completed', 'true');
        sessionStorage.removeItem(SESSION_OPEN_REGISTRATION_FEEDBACK_AFTER_TUTORIAL);
        if (sessionStorage.getItem(SESSION_FIRST_TIME_AWAITING_BOOKMARK) === 'true') {
            sessionStorage.removeItem(SESSION_FIRST_TIME_AWAITING_BOOKMARK);
            sessionStorage.setItem(SESSION_PENDING_BOOKMARK_AFTER_ANALYSIS, 'true');
            window.dispatchEvent(new CustomEvent(FIRST_TIME_DISMISS_TUTORIAL_OVERLAY));
        }
    };

    return (
        <>
            {children}

            {showOnboarding && (
                <OnboardingManager>
                    <div />
                </OnboardingManager>
            )}

            <RegistrationFeedbackModal
                isOpen={showRegistrationFeedback}
                onClose={completeRegistrationFeedback}
            />

            <BookmarkGuideModal
                isOpen={showBookmarkGuide}
                onClose={closeBookmarkGuide}
                isMobile={isMobileViewport}
            />
        </>
    );
};

export default FirstTimeExperienceManager;
